import { ModuleOutlet, RequireAuth, type ModuleOutletSlots } from "@crialume/core/react";
import { createBrowserRouter } from "react-router";
import { CHANGE_PASSWORD_PATH } from "./paths";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import type { SessionReady } from "./session-ready";
import {
  FirstModuleRedirect,
  ForbiddenPage,
  ModuleFailure,
  ModuleLoading,
  NotFoundPage,
} from "../pages/status-pages";

const outletSlots: ModuleOutletSlots = {
  loading: <ModuleLoading />,
  notFound: <NotFoundPage />,
  forbidden: <ForbiddenPage />,
  error: (error, retry) => <ModuleFailure error={error} retry={retry} />,
};
// Cada tela vem no próprio chunk: quem abre a landing não baixa o app logado, e vice-versa.
const AppLayout = lazy(() =>
  import("../layout/app-layout").then((m) => ({ default: m.AppLayout })),
);
const LoginPage = lazy(() => import("../pages/login-page").then((m) => ({ default: m.LoginPage })));
const SignUpPage = lazy(() =>
  import("../pages/sign-up-page").then((m) => ({ default: m.SignUpPage })),
);
const InvitePage = lazy(() =>
  import("../pages/invite-page").then((m) => ({ default: m.InvitePage })),
);
const VerifyEmailPage = lazy(() =>
  import("../pages/verify-email-page").then((m) => ({ default: m.VerifyEmailPage })),
);
const ChangePasswordPage = lazy(() =>
  import("../pages/change-password-page").then((m) => ({ default: m.ChangePasswordPage })),
);
const HelpPage = lazy(() => import("../onboarding/help-page"));
const ManageVideosPage = lazy(() => import("../onboarding/manage-videos-page"));
const LandingPage = lazy(() => import("../landing/landing-page"));
const NatalPage = lazy(() => import("../natal/natal-page"));
const ShopPage = lazy(() => import("../shop/shop-page"));
const CheckoutPage = lazy(() => import("../shop/checkout-page"));
const PurchasesPage = lazy(() => import("../shop/purchases-page"));
const ManageSalesPage = lazy(() => import("../shop/manage-sales-page"));

function Loading({ children }: { children: ReactNode }) {
  return <Suspense fallback={<ModuleLoading />}>{children}</Suspense>;
}

/**
 * A landing desenha antes de a sessão voltar pelo cookie. As rotas protegidas esperam essa
 * resposta: sem ela, o `RequireAuth` mandaria para o login quem ainda tem sessão.
 */
function SessionGate({ session, children }: { session: SessionReady; children: ReactNode }) {
  const [ready, setReady] = useState(session.settled);
  useEffect(() => {
    if (ready) return;
    let active = true;
    void session.promise.then(() => active && setReady(true));
    return () => {
      active = false;
    };
  }, [ready, session]);
  return ready ? children : <ModuleLoading />;
}

export function createAppRouter(session: SessionReady) {
  // BASE_URL vem do `base` do Vite (VITE_BASE_PATH) — nada de basename fixo no código.
  const basename = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

  return createBrowserRouter(
    [
      {
        path: "/natal",
        element: (
          <Loading>
            <NatalPage />
          </Loading>
        ),
      },
      {
        path: "/moldes",
        element: (
          <Suspense fallback={<ModuleLoading />}>
            <ShopPage />
          </Suspense>
        ),
      },
      {
        path: "/checkout/:kind/:productId",
        element: (
          <Suspense fallback={<ModuleLoading />}>
            <CheckoutPage />
          </Suspense>
        ),
      },
      {
        path: "/login",
        element: (
          <Loading>
            <LoginPage />
          </Loading>
        ),
      },
      {
        path: "/convite/:token",
        element: (
          <Loading>
            <InvitePage />
          </Loading>
        ),
      },
      {
        path: "/confirmar-email/:token",
        element: (
          <Loading>
            <VerifyEmailPage />
          </Loading>
        ),
      },
      {
        path: "/cadastro",
        element: (
          <Loading>
            <SignUpPage />
          </Loading>
        ),
      },
      {
        // Primeiro acesso (senha provisória) ou troca voluntária.
        path: CHANGE_PASSWORD_PATH,
        element: (
          <SessionGate session={session}>
            <RequireAuth passwordChangePath={CHANGE_PASSWORD_PATH}>
              <Loading>
                <ChangePasswordPage />
              </Loading>
            </RequireAuth>
          </SessionGate>
        ),
      },
      {
        path: "/app",
        element: (
          <SessionGate session={session}>
            <RequireAuth passwordChangePath={CHANGE_PASSWORD_PATH}>
              <Loading>
                <AppLayout />
              </Loading>
            </RequireAuth>
          </SessionGate>
        ),
        children: [
          { index: true, element: <FirstModuleRedirect /> },
          {
            path: "compras",
            element: (
              <Suspense fallback={<ModuleLoading />}>
                <PurchasesPage />
              </Suspense>
            ),
          },
          {
            path: "vendas",
            element: (
              <Suspense fallback={<ModuleLoading />}>
                <ManageSalesPage />
              </Suspense>
            ),
          },
          {
            path: "ajuda",
            element: (
              <Suspense fallback={<ModuleLoading />}>
                <HelpPage />
              </Suspense>
            ),
          },
          {
            path: "ajuda/videos",
            element: (
              <Suspense fallback={<ModuleLoading />}>
                <ManageVideosPage />
              </Suspense>
            ),
          },
          { path: ":moduleId/*", element: <ModuleOutlet slots={outletSlots} /> },
        ],
      },
      {
        path: "/",
        element: (
          <Suspense fallback={<ModuleLoading />}>
            <LandingPage />
          </Suspense>
        ),
      },
      { path: "*", element: <NotFoundPage /> },
    ],
    { basename },
  );
}
